import { useDisclosure } from "@chakra-ui/react";
import { CourseService } from "@root/services/course";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNotification } from "./useNotification";
import {
  COURSE_STATUS,
  PAGINATION_DEFAULTS,
  RECORDS_PER_PAGE_COURSES,
  EnrollmentOptionType,
} from "@root/constants";
import {
  splitFileIntoChunks,
  createChunkFormData,
  ChunkInfo,
  convertObjectToFormData,
} from "@root/utils/fileChunking";

const getInitialStep = () => {
  const stored = localStorage.getItem("currentStep");
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (error) {
      console.error("Failed to parse currentCourse:", error);
    }
  }
  return 0;
};

const getInitialCourse = () => {
  const stored = localStorage.getItem("currentCourse");
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (error) {
      console.error("Failed to parse currentCourse:", error);
    }
  }
  // Fallback default
  return null;
};

export const useCourseManagement = () => {
  const [getCategoriesLoading, setGetCategoriesLoading] = useState(false);
  const [categoriesData, setCategoriesData] = useState(null);
  const [freeCategoriesData, setFreeCategoriesData] = useState(null);
  const [step1Data, setStep1Data] = useState<FormValuesStep1 | null>(null);
  const [step2Data, setStep2Data] = useState<any>(null);
  const [step3Data, setStep3Data] = useState<FormValuesStep3 | null>(null);
  const [deleteFaqLoading, setDeleteFaqLoading] = useState(false);
  const [coursesData, setCoursesData] = useState([]);
  const [courseData, setCourseData] = useState(null);
  const [freeCoursesData, setFreeCoursesData] = useState([]);
  const [freeCourseData, setFreeCourseData] = useState(null);
  const [addCourseLoading, setAddCourseLoading] = useState(false);
  const [updateCourseLoading, setUpdateCourseLoading] = useState(false);
  const [deleteCourseLoading, setDeleteCourseLoading] = useState(false);
  const [duration, setDuration] = useState(false);
  const [getCoursesLoading, setGetCoursesLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [uploadThumbnailLoading, setUploadThumbnailLoading] = useState(false);
  const [thumbnailRes, setThumbnailRes] = useState<any>(null);
  // Initialize with valid Date objects
  const [startDate, setStartDate] = useState<Date | null>(() => new Date());
  const [endDate, setEndDate] = useState<Date | null>(() => {
    // Set end date to tomorrow by default
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  });
  const [currentStep, setCurrentStep] = useState(getInitialStep);
  const [isStep2Disabled, setIsStep2Disabled] = useState(false);
  const [selectedThumbnail, setSelectedThumbnail] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [addChapterLoading, setAddChapterLoading] = useState(false);
  const [updateChapterLoading, setUpdateChapterLoading] = useState(false);
  const [updateVideoLoading, setUpdateVideoLoading] = useState(false);
  const [deleteChapterLoading, setDeleteChapterLoading] = useState(false);
  const [getCourseChaptersLoading, setGetCourseChaptersLoading] =
    useState(false);
  const [addVideoLoading, setAddVideoLoading] = useState(false);
  const [isUploadingChunks, setIsUploadingChunks] = useState(false);
  const [enrollmentPeriodStart, setEnrollmentPeriodStart] = useState(null);
  const [enrollmentPeriodEnd, setEnrollmentPeriodEnd] = useState(null);
  const [currentCourse, setCurrentCourse] = useState<any>(getInitialCourse);
  const [courseChaptersData, setCourseChaptersData] = useState(null);
  const [updateCourseId, setUpdateCourseId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [lastLoginDates, setLastLoginDates] = useState("");
  const [addCourseEnrollLoading, setAddCourseEnrollLoading] = useState(false);
  const [watchLaterLoading, setWatchLaterLoading] = useState(false);
  const [deleteVideoLoading, setDeleteVideoLoading] = useState(false);
  const [chapters, setChapters] = useState<Chapter[]>([
    {
      id: Date.now.toString(),
      title: "",
      duration: "",
      description: "",
      videos: [
        // {
        //   id: "1",
        //   title: "",
        //   isPublic: true,
        //   uploadProgress: 0,
        //   isUploading: false,
        //   file: undefined,
        // },
      ],
    },
  ]);
  const [getCourseProgressLoading, setGetCourseProgressLoading] =
    useState(false);
  const [courseProgressData, setCourseProgressData] = useState<any[]>([]);
  const [videoData, setVideoData] = useState<any | null>(null);

  useEffect(() => {
    //localStorage.setItem("currentStep", JSON.stringify(currentStep));
    //localStorage.setItem("currentCourse", JSON.stringify(currentCourse));
  }, [currentStep, currentCourse]);

  const methods1 = useForm<FormValuesStep1>({
    mode: "onTouched",
  });

  const {
    register: register1,
    handleSubmit: handleSubmit1,
    control: control1,
    watch: watch1,
    setValue: setValue1,
    reset,
  } = methods1;

  function resetData() {
    // Reset step
    setCurrentStep(0);

    // Reset current course
    setCurrentCourse(null);
    setCourseData(null);
    setUpdateCourseId(null);

    // Reset chapters
    setChapters([
      {
        id: Date.now().toString(),
        title: "",
        duration: "",
        description: "",
        videos: [],
      },
    ]);
    setCourseChaptersData(null);

    // Reset step data
    setStep1Data(null);
    setStep2Data(null);
    setStep3Data(null);

    // Reset thumbnail
    setSelectedThumbnail(null);
    setThumbnailRes(null);

    // In resetData() - now resets the modal state
    onCloseSuccess();
    setProgress(0);

    // Reset dates
    setStartDate(new Date());
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setEndDate(tomorrow);

    // Reset upload states
    setUploadProgress(0);
    setIsUploadingChunks(false);
    setIsUploading(false);

    // Reset disabled states
    setIsStep2Disabled(false);

    // Reset form 1 (CourseStep1)
    reset({
      title: "",
      description: "",
      category: "",
      requirement: "",
      courseSubscriberType: 1,
      companiesIds: [],
    });

    // Reset form 3 (CourseStep3)
    reset3({
      makeWholeCourseFree: false,
      selectedChapters: {},
      selectedVideos: {},
      displayOnHome: false,
      freeAccessEnabled: false,
      freeAccessChapters: [],
      enrollmentOption: "duration",
      enrollmentDurationValue: "",
      enrollmentStartDate: "",
      enrollmentEndDate: "",
    });

    // Reset selected category
    setSelectedCategory(null);
  }

  const methods3 = useForm<FormValuesStep3>({
    mode: "onTouched",
    defaultValues: {
      makeWholeCourseFree: false,
      selectedChapters: {},
      selectedVideos: {},
      displayOnHome: false,
      freeAccessEnabled: false,
      freeAccessChapters: [],
      enrollmentOption: "duration", // Set a default enrollment option
      enrollmentDurationValue: "",
      enrollmentStartDate: "",
      enrollmentEndDate: "",
    },
  });

  const { watch: watch3, reset: reset3 } = methods3;

  function hasTrueValue(obj: Object): boolean {
    return Object.values(obj).some((value) => value === true);
  }

  const displayOnHome = watch3("displayOnHome");
  const hasChaptersSelected = hasTrueValue(watch3("selectedChapters"));
  const enrollmentDuration = watch3("enrollmentDurationValue");
  const enrollmentOption = watch3("enrollmentOption");

  const isStep3Disabled = !(
    (displayOnHome || hasChaptersSelected) &&
    enrollmentOption &&
    (enrollmentOption !== "duration" || enrollmentDuration)
  );

  const thumbnail1 = watch1("thumbnail");
  const { t } = useTranslation("common");
  const { showNotification } = useNotification();
  const courseService = new CourseService();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isOpenSuccess,
    onOpen: onOpenSuccess,
    onClose: onCloseSuccess,
  } = useDisclosure();

  const fetchCategories = async (payload: any) => {
    setGetCategoriesLoading(true);
    try {
      const res = await courseService.fetchCategories(payload);
      setCategoriesData(res?.data?.data);
    } catch (error: unknown) {
      console.warn(error);
    } finally {
      setGetCategoriesLoading(false);
    }
  };

  const fetchFreeCategories = async (payload: any) => {
    setGetCategoriesLoading(true);
    try {
      const res = await courseService.fetchFreeCategories(payload);
      setFreeCategoriesData(res?.data?.data);
    } catch (error: unknown) {
      console.warn(error);
    } finally {
      setGetCategoriesLoading(false);
    }
  };

  const onDateChange = (date: any) => {
    setSearch("");
    setLastLoginDates(date);
    if (date) {
      const [start_date, end_date] = date;
      if (start_date && end_date) {
        const formattedStart = start_date.split("T")[0];
        const formattedEnd = end_date.split("T")[0];

        setEnrollmentPeriodStart(formattedStart);
        setEnrollmentPeriodEnd(formattedEnd);

        fetchCourses({
          page: page,
          limit: PAGINATION_DEFAULTS.PAGESIZE,
          enrollmentPeriodStart: formattedStart,
          enrollmentPeriodEnd: formattedEnd,
        });
      }
    }
    if (date === null) {
      setPage(1);
      fetchCourses({
        page: page,
        searchQuery: search,
        limit: PAGINATION_DEFAULTS.PAGESIZE,
      });
    }
  };

  const fetchCourses = async (payload: any = {}) => {
    setGetCoursesLoading(true);
    try {
      const pageSize = RECORDS_PER_PAGE_COURSES;
      const mergedPayload = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
        search: search,
        ...payload,
      };
      const res = await courseService.fetchCourses(mergedPayload);
      setCoursesData(res?.data?.data);
      setPageCount(res?.data?.data?.count || 0);
    } catch (error: unknown) {
      showNotification({
        type: "error",
        message: t("courseManagement.fetchError"),
      });
    } finally {
      setGetCoursesLoading(false);
    }
  };

  const fetchCourse = async (courseId: number) => {
    setGetCoursesLoading(true);
    try {
      const res = await courseService.fetchCourse(courseId);
      setCourseData(res?.data?.data);
      // Reset to step 1 when editing a course
      // setCurrentStep(0);
    } catch (error: unknown) {
      showNotification({
        type: "error",
        message: t("courseManagement.fetchError"),
      });
    } finally {
      setGetCoursesLoading(false);
    }
  };

  const fetchFreeCourses = async (payload: any = {}) => {
    setGetCoursesLoading(true);
    try {
      const pageSize = RECORDS_PER_PAGE_COURSES;
      const mergedPayload = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
        search: search,
        ...payload,
      };
      const res = await courseService.fetchFreeCourses(mergedPayload);
      setFreeCoursesData(res?.data?.data);
      setPageCount(res?.data?.data?.count || 0);
    } catch (error: unknown) {
      showNotification({
        type: "error",
        message: t("courseManagement.fetchError"),
      });
    } finally {
      setGetCoursesLoading(false);
    }
  };

  const fetchFreeCourse = async (courseId: number) => {
    setGetCoursesLoading(true);
    try {
      const res = await courseService.fetchFreeCourse(courseId);
      setFreeCourseData(res?.data?.data);
    } catch (error: unknown) {
      showNotification({
        type: "error",
        message: t("courseManagement.fetchError"),
      });
    } finally {
      setGetCoursesLoading(false);
    }
  };
  const updateCourse = async (
    id: number,
    payload: any,
    step?: number,
    onNavigateBack?: () => void
  ) => {
    setUpdateCourseLoading(true);
    try {
      const res = await courseService.updateCourse(id, payload);
      if (step === 1) {
        // Step 1 seeds its fields from courseData every time it mounts, so the
        // saved course has to be re-read here. Without it, coming back to step 1
        // restores the selection as it was before this save — most visibly, a
        // cleared organization list fills itself back in.
        await fetchCourse(id);
        resetStep1();
        setCurrentStep(1);
      }
      if (step === 3) {
        if (!updateCourseId) {
          onOpenSuccess();
        } else {
          showNotification({
            type: "success",
            message: t("courseManagement.updateSuccess"),
          });
          window.history.back();
        }
      }
      return res;
    } catch (err) {
      showNotification({
        type: "error",
        message: t("courseManagement.updateError"),
      });
      return false;
    } finally {
      setUpdateCourseLoading(false);
      return false;
    }
  };

  const addCourse = async (payload: any) => {
    setAddCourseLoading(true);
    try {
      const res = await courseService.addCourse(payload);
      showNotification({
        type: "success",
        message: t("courseManagement.addSuccess"),
      });
      const currentCourse = res?.data?.data;
      setCurrentCourse(currentCourse);
      const courseId = res?.data?.data?.id;
      const updatePayload = {
        status: COURSE_STATUS.DRAFT,
        step: 2,
      };
      await updateCourse(courseId, updatePayload);
      resetStep1();
      setCurrentStep(1);
    } catch (err) {
      showNotification({
        type: "error",
        message: t("courseManagement.addError"),
      });
    } finally {
      setAddCourseLoading(false);
    }
  };

  const deleteCourse = async (id: number, refreshPayload?: any) => {
    setDeleteCourseLoading(true);
    try {
      await courseService.deleteCourse(id);
      showNotification({
        type: "success",
        message: t("courseManagement.deleteSuccess"),
      });
      // Refresh the courses data after successful deletion
      if (refreshPayload) {
        // Use the provided payload with current filters
        fetchCourses(refreshPayload);
      } else {
        // Fallback to basic refresh
        const currentPayload = {
          limit: 10,
          offset: (page - 1) * 10,
          search: search,
        };
        fetchCourses(currentPayload);
      }
    } catch (err) {
      showNotification({
        type: "error",
        message: t("courseManagement.deleteError"),
      });
    } finally {
      setDeleteCourseLoading(false);
    }
  };

  const uploadThumbnail = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    if (updateCourseId) {
      formData.append("courseId", updateCourseId);
    }
    try {
      const res = await courseService.uploadThumbnail(formData);
      setThumbnailRes(res?.data?.data);
    } catch (err) {
      showNotification({
        type: "error",
        message: t("courseManagement.thumbnailError"),
      });
    } finally {
      setUploadThumbnailLoading(false);
      setIsUploading(false);
    }
  };

  const handleThumbnailSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file && event.target.files) {
      setSelectedThumbnail(file);
      uploadThumbnail(file);
    }
  };

  const addChapters = async (payload: any) => {
    setAddCourseLoading(true);
    try {
      await courseService.publishCourse(payload);
      showNotification({
        type: "success",
        message: t("courseManagement.chaptersAddSuccess"),
      });
    } catch (err) {
      showNotification({
        type: "error",
        message: t("courseManagement.chaptersAddError"),
      });
    } finally {
      setAddCourseLoading(false);
    }
  };

  const publishCourse = async (payload: any) => {
    setAddCourseLoading(true);
    try {
      await courseService.publishCourse(payload);
      showNotification({
        type: "success",
        message: t("courseManagement.publishSuccess"),
      });
    } catch (err) {
      showNotification({
        type: "error",
        message: t("courseManagement.publishError"),
      });
    } finally {
      setAddCourseLoading(false);
    }
  };

  const onStep1Submit = (data: FormValuesStep1) => {
    setStep1Data(data);
    function calculateCompanies() {
      if (updateCourseId && !data?.companiesIds?.length) {
        return [0];
      }
      return data?.companiesIds;
    }
    const payload: any = {
      title: data?.title,
      description: data?.description,
      requirement: Number(data?.requirement),
      step: 1,
      categoryId: Number(selectedCategory?.[0]),
      subcategoryId: Number(selectedCategory?.[1]),
      companiesIds: calculateCompanies(),
      thumbnail:
        thumbnailRes?.filePath && !updateCourseId
          ? thumbnailRes?.filePath
          : undefined,
      courseSubscriberType: Number(data?.courseSubscriberType),
    };
    if (updateCourseId) {
      delete payload.step;
      updateCourse(Number(updateCourseId), payload, 1);
    } else {
      addCourse(payload);
    }
  };

  const onStep2Submit = (data: any) => {
    // Validate that all saved chapters have at least one saved video
    const chaptersWithoutVideos = chapters.filter((chapter: Chapter) => {
      // Only check chapters that are saved (have chapterId)
      if (!chapter?.chapterId) {
        return false; // Skip unsaved chapters
      }

      // Check if chapter has at least one saved video (video with videoId)
      const hasSavedVideo = chapter.videos?.some(
        (video: any) => video?.videoId
      );
      return !hasSavedVideo;
    });

    if (chaptersWithoutVideos.length > 0) {
      // Show error notification
      showNotification({
        type: "error",
        message: t("courseManagement.chapterVideoRequired"),
      });
      return; // Don't proceed to next step
    }

    setStep2Data(data);
    setCurrentStep(2);
  };

  const onStep3Submit = (
    data: FormValuesStep3,
    onNavigateBack?: () => void
  ) => {
    setStep3Data(data);
    const payload: any = {
      displayOnHome: data?.displayOnHome,
      freeAccessEnabled: data?.freeAccessEnabled,
      courseVideos: Object.entries(data?.selectedVideos ?? {}).map(
        ([key, value]) => ({
          videoId: Number(key),
          isFree: value,
        })
      ),
      status: COURSE_STATUS.PUBLISHED,
      step: 3,
    };
    // Helper function to convert any date value to ISO string format
    const toISOString = (
      dateValue: Date | string | null | undefined
    ): string | null => {
      if (!dateValue) return null;

      let dateObj: Date;
      if (dateValue instanceof Date) {
        dateObj = dateValue;
      } else if (typeof dateValue === "string") {
        const trimmed = dateValue.trim();
        if (trimmed === "") return null;
        dateObj = new Date(trimmed);
      } else {
        return null;
      }

      // Check if date is valid
      if (isNaN(dateObj.getTime())) return null;

      // Return ISO string format: "2026-01-14T12:42:47.000Z"
      return dateObj.toISOString();
    };

    // Handle enrollment options - ensure only one is set, the other is null
    // Check which option is selected based on enrollmentOption field
    const enrollmentOption = data?.enrollmentOption;
    const enrollmentDurationValue = data?.enrollmentDurationValue;

    if (
      enrollmentOption === EnrollmentOptionType.duration &&
      enrollmentDurationValue &&
      enrollmentDurationValue !== "" &&
      enrollmentDurationValue !== null &&
      enrollmentDurationValue !== undefined
    ) {
      // If duration option is selected and has a value, send duration and set dates to null
      payload.enrollmentDuration = String(enrollmentDurationValue);
      payload.enrollmentPeriodStart = null;
      payload.enrollmentPeriodEnd = null;
    } else if (enrollmentOption === EnrollmentOptionType.period) {
      // If period option is selected, send dates and set duration to null
      // Always use context state (startDate/endDate) as source of truth for period option
      let periodStart: string | null = null;
      let periodEnd: string | null = null;

      // Priority 1: Use context state (startDate/endDate) - these are updated by Schedule component
      periodStart = toISOString(startDate);
      periodEnd = toISOString(endDate);

      // Priority 2: Fallback to form data if context state doesn't have dates
      if (!periodStart && data?.enrollmentStartDate) {
        periodStart = toISOString(data.enrollmentStartDate);
      }
      if (!periodEnd && data?.enrollmentEndDate) {
        periodEnd = toISOString(data.enrollmentEndDate);
      }

      // Always set duration to null when period option is selected
      payload.enrollmentDuration = null;

      // Set the dates - use the values we found (in ISO format)
      payload.enrollmentPeriodStart = periodStart;
      payload.enrollmentPeriodEnd = periodEnd;
    } else {
      // If neither option is properly selected, set all to null
      payload.enrollmentDuration = null;
      payload.enrollmentPeriodStart = null;
      payload.enrollmentPeriodEnd = null;
    }
    if (updateCourseId) {
      delete payload.step;
      //delete payload.status;
    }
    updateCourse(updateCourseId ?? currentCourse?.id, payload, 3, () => {
      // ✅ After success: clear everything
      resetAllSteps();
      if (onNavigateBack) onNavigateBack();
    });
  };

  const resetAllSteps = () => {
    reset(); // react-hook-form reset
    setThumbnailRes(null);
    setSelectedThumbnail(null);
    setStep1Data(null);
    setStep2Data(null);
    setStep3Data(null);

    setChapters([
      {
        id: Date.now().toString(),
        title: "",
        duration: "",
        description: "",
        videos: [],
      },
    ]);
    setCourseChaptersData(null);

    setUpdateCourseId(null);
    setCourseData(null);
    setCurrentStep(0);
  };

  const resetStep1 = () => {
    reset();
    setThumbnailRes(null);
    setSelectedThumbnail(null);
  };

  const resetStep2 = () => {
    setChapters([
      {
        id: Date.now.toString(),
        title: "",
        duration: "",
        description: "",
        videos: [],
      },
    ]);
    setCourseChaptersData(null);
  };

  const addCourseChapter = async (payload: any) => {
    setAddChapterLoading(true);
    try {
      const res = await courseService.addCourseChapter(payload);
      showNotification({
        type: "success",
        message: t("courseManagement.chapterAddSuccess"),
      });
      const chapterId = res?.data?.data?.id;
      setChapters((prevChapters: Chapter[]) => {
        return prevChapters?.map((chapter: Chapter) => {
          if (!chapter?.chapterId) {
            return {
              ...chapter,
              chapterId,
            };
          }
          return chapter;
        });
      });
    } catch (err) {
      showNotification({
        type: "error",
        message: t("courseManagement.chapterAddError"),
      });
    } finally {
      setAddChapterLoading(false);
    }
  };

  const updateCourseChapter = async (id: number, payload: any) => {
    setUpdateChapterLoading(true);
    try {
      const res = await courseService.updateCourseChapter(id, payload);
      showNotification({
        type: "success",
        message: t("courseManagement.chapterAddSuccess"),
      });
    } catch (err) {
      showNotification({
        type: "error",
        message: t("courseManagement.chapterAddError"),
      });
    } finally {
      setUpdateChapterLoading(false);
    }
  };

  const getCourseChapters = async (
    courseId: number,
    options?: { isSignedUrl?: boolean }
  ) => {
    setGetCourseChaptersLoading(true);
    try {
      const res = await courseService.fetchCourseChapters(courseId, options);
      setCourseChaptersData(res?.data?.data);
    } catch (err) {
      console.error(err);
    } finally {
      setGetCourseChaptersLoading(false);
    }
  };

  const deleteCourseChapter = async (id: number) => {
    setDeleteChapterLoading(true);
    try {
      const res = await courseService.deleteCourseChapter(id);
      showNotification({
        type: "success",
        message: t("courseManagement.chapterDeleteSuccess"),
      });
    } catch (err) {
      showNotification({
        type: "error",
        message: t("courseManagement.chapterDeleteError"),
      });
    } finally {
      setDeleteChapterLoading(false);
    }
  };

  const addCourseVideo = async (
    courseId: any,
    chapterId: any,
    videoData: any
  ) => {
    setAddVideoLoading(true);
    const formData = convertObjectToFormData(videoData);
    try {
      const res = await courseService.addCourseVideo(
        courseId,
        chapterId,
        formData
      );
      showNotification({
        type: "success",
        message: t("courseManagement.videoAddSuccess"),
      });

      const videoId = res?.data?.data?.id;

      if (videoId) {
        setChapters((prevChapters: Chapter[]) => {
          return prevChapters?.map((chapter: Chapter) => {
            if (Number(chapter?.chapterId) === Number(chapterId)) {
              return {
                ...chapter,
                videos: chapter?.videos?.map((video) => {
                  if (!video?.videoId) {
                    return {
                      ...video,
                      videoId,
                    };
                  }
                  return video;
                }),
              };
            }
            return chapter;
          });
        });
      }

      // Refresh from server to ensure sync
      await getCourseChapters(courseId);
    } catch (err: any) {
      showNotification({
        type: "error",
        message: err?.response?.data?.message || t("courseManagement.videoAddError"),
      });
    } finally {
      setAddVideoLoading(false);
    }
  };

  /**
   * Uploads a video file in chunks of 20MB for better reliability and progress tracking
   * @param courseId - The course ID
   * @param chapterId - The chapter ID
   * @param videoData - Video metadata (title, size, duration, etc.)
   * @param file - The video file to upload
   */
  const addCourseVideoChunked = async (
    courseId: any,
    chapterId: any,
    videoData: any
  ) => {
    setIsUploadingChunks(true);
    setUploadProgress(0);
    const file = videoData?.file;

    try {
      // Split file into chunks
      const chunks = splitFileIntoChunks(file);
      let lastResponse: any = null;

      // Upload chunks sequentially
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        const chunkFormData = createChunkFormData(chunk, videoData);

        // Upload current chunk and store response
        lastResponse = await courseService.addCourseVideo(
          courseId,
          chapterId,
          chunkFormData
        );

        // Update progress
        const progress = Math.round(((i + 1) / chunks.length) * 100);
        setUploadProgress(progress);
      }

      console.log("All chunks uploaded successfully", lastResponse);

      showNotification({
        type: "success",
        message: t("courseManagement.videoAddSuccess"),
      });

      // Get the videoId from the last response
      const videoId = lastResponse?.data?.data?.id;

      // Update chapters state with the new videoId
      if (videoId) {
        setChapters((prevChapters: Chapter[]) => {
          return prevChapters?.map((chapter: Chapter) => {
            if (chapter?.chapterId === chapterId) {
              return {
                ...chapter,
                videos: chapter?.videos?.map((video) => {
                  // Find the video that was just uploaded (has file but no videoId)
                  if (video?.file && !video?.videoId) {
                    return {
                      ...video,
                      videoId,
                      file: undefined, // Clear the file after successful upload
                      isUploading: false,
                      uploadProgress: 100,
                    };
                  }
                  return video;
                }),
              };
            }
            return chapter;
          });
        });
      }

      // Also refresh from server
      await getCourseChapters(courseId);
    } catch (err: any) {
      console.error("Chunked upload error:", err);
      showNotification({
        type: "error",
        message: err?.response?.data?.message || t("courseManagement.videoAddError"),
      });
    } finally {
      setIsUploadingChunks(false);
      setUploadProgress(0);
    }
  };

  const updateCourseVideo = async (
    courseId: number,
    chapterId: number,
    videoId: number,
    videoData: any
  ) => {
    setUpdateVideoLoading(true);
    const formData = convertObjectToFormData(videoData);
    // Only add videoId to formData if it's not a file upload (local file uploads don't need videoId)
    if (!videoData?.file) {
      formData.append("videoId", videoId.toString());
    }
    try {
      const res = await courseService.updateCourseVideo(
        courseId,
        chapterId,
        formData
      );
      showNotification({
        type: "success",
        message: t("courseManagement.videoUpdateSuccess"),
      });
    } catch (err) {
      showNotification({
        type: "error",
        message: t("courseManagement.videoUpdateError"),
      });
    } finally {
      setUpdateVideoLoading(false);
    }
  };

  const updateCourseVideoChunked = async (
    courseId: number,
    chapterId: number,
    videoId: number,
    videoData: any
  ) => {
    setIsUploadingChunks(true);
    setUploadProgress(0);
    const file = videoData?.file;

    try {
      // Split file into chunks
      const chunks = splitFileIntoChunks(file);

      // Upload chunks sequentially with intervals
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        const chunkFormData = createChunkFormData(chunk, videoData);
        // Don't add videoId for local file uploads (new file uploads don't need videoId)
        // videoId is only needed for metadata updates, not file uploads

        // Upload current chunk
        await courseService.updateCourseVideo(
          courseId,
          chapterId,
          chunkFormData
        );

        // Update progress
        const progress = Math.round(((i + 1) / chunks.length) * 100);
        setUploadProgress(progress);

        // Add delay between chunks (same as addCourseVideoChunked)
        if (i < chunks.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 1000)); // 1 second delay
        }
      }

      showNotification({
        type: "success",
        message: t("courseManagement.videoUpdateSuccess"),
      });

      // Clear the file from chapters state after successful upload
      setChapters((prevChapters: Chapter[]) => {
        return prevChapters?.map((chapter: Chapter) => {
          if (Number(chapter?.chapterId) === Number(chapterId)) {
            return {
              ...chapter,
              videos: chapter?.videos?.map((video) => {
                if (Number(video?.videoId) === Number(videoId)) {
                  return {
                    ...video,
                    file: undefined, // Clear the file after successful upload
                    isUploading: false,
                    uploadProgress: 100,
                  };
                }
                return video;
              }),
            };
          }
          return chapter;
        });
      });

      // Refresh from server
      await getCourseChapters(courseId);
    } catch (err) {
      console.error("Chunked upload error:", err);
      showNotification({
        type: "error",
        message: t("courseManagement.videoUpdateError"),
      });
    } finally {
      setIsUploadingChunks(false);
      setUploadProgress(0);
    }
  };

  const deleteCourseVideo = async (chapterId: number, videoId: number) => {
    setDeleteVideoLoading(true);
    try {
      await courseService.deleteCourseVideo(videoId);
      showNotification({
        type: "success",
        message: t("courseManagement.videoDeleteSuccess"),
      });
      setChapters((prevChapters) =>
        prevChapters?.map((chapter: Chapter) =>
          Number(chapter?.chapterId) === Number(chapterId)
            ? {
                ...chapter,
                videos: chapter?.videos?.filter(
                  (video) => video?.videoId !== videoId
                ),
              }
            : chapter
        )
      );
    } catch (err) {
      showNotification({
        type: "error",
        message: t("courseManagement.videoDeleteError"),
      });
    } finally {
      setDeleteVideoLoading(false);
    }
  };

  const uploadCourseEnrollData = async (
    courseId: number,
    payload: any,
    isWatchLater: boolean = false
  ) => {
    if (isWatchLater) {
      setWatchLaterLoading(true);
    } else {
      setAddCourseEnrollLoading(true);
    }
    try {
      const res = await courseService.postCourseEnrollData(courseId, payload);
      return res;
    } catch (err) {
      showNotification({
        type: "error",
        message: t("courseManagement.enrollError"),
      });
    } finally {
      if (isWatchLater) {
        setWatchLaterLoading(false);
      } else {
        setAddCourseEnrollLoading(false);
      }
    }
  };

  const updateCourseEnrollData = async (
    courseId: number,
    id: number,
    payload: any,
    isWatchLater: boolean = false
  ) => {
    if (isWatchLater) {
      setWatchLaterLoading(true);
    } else {
      setAddCourseEnrollLoading(true);
    }
    try {
      const res = await courseService.updateCourseEnrollData(
        courseId,
        id,
        payload
      );
      return res;
    } catch (err) {
      showNotification({
        type: "error",
        message: t("courseManagement.enrollError"),
      });
    } finally {
      if (isWatchLater) {
        setWatchLaterLoading(false);
      } else {
        setAddCourseEnrollLoading(false);
      }
    }
  };

  const uploadCourseProgressData = async (payload: any) => {
    setAddCourseEnrollLoading(true);
    try {
      const res = await courseService.postCourseProgressData(payload);
      return res;
    } catch (err) {
      showNotification({
        type: "error",
        message: t("courseManagement.progressError"),
      });
      return null;
    } finally {
      setAddCourseEnrollLoading(false);
    }
  };

  const getCourseProgressById = async (courseId: number) => {
    console.log(`🌐 [API CALL] Fetching course progress for courseId: ${courseId}`);
    setGetCourseProgressLoading(true);
    try {
      const res = await courseService.getCourseTakerProgressById(courseId);
      console.log(`🌐 [API RESPONSE] Course progress API response:`, res?.data?.data);
      setCourseProgressData(res?.data?.data || []);
    } catch (err) {
      console.error(`❌ [API ERROR] Failed to fetch course progress:`, err);
      showNotification({
        type: "error",
        message: t("courseManagement.progressError"),
      });
    } finally {
      setGetCourseProgressLoading(false);
    }
  };

  const fetchVideoUrlById = async (id: number) => {
    setGetCourseProgressLoading(true);
    try {
      const res = await courseService.getVideoUrlById(id);
      setVideoData(res?.data?.data || []);
    } catch (err) {
      showNotification({
        type: "error",
        message: t("courseManagement.progressError"),
      });
    } finally {
      setGetCourseProgressLoading(false);
    }
  };

  return {
    thumbnail1,
    selectedThumbnail,
    methods1,
    register1,
    handleSubmit1,
    control1,
    watch1,
    getCategoriesLoading,
    categoriesData,
    fetchCategories,
    setCategoriesData,
    onDateChange,
    isOpen,
    onOpen,
    onClose,
    addCourseLoading,
    getCoursesLoading,
    updateCourseLoading,
    deleteCourseLoading,
    addCourse,
    updateCourse,
    deleteCourse,
    fetchCourses,
    coursesData,
    search,
    setSearch,
    page,
    setPage,
    pageCount,
    setPageCount,
    selectedCategory,
    setSelectedCategory,
    handleThumbnailSelect,
    thumbnailRes,
    onStep1Submit,
    addChapters,
    publishCourse,
    onStep3Submit,
    methods3,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    currentStep,
    setDuration,
    setCurrentStep,
    isStep3Disabled,
    onStep2Submit,
    isStep2Disabled,
    setIsStep2Disabled,
    uploadProgress,
    isUploading,
    addCourseChapter,
    addChapterLoading,
    currentCourse,
    setCurrentCourse,
    resetData,
    chapters,
    setChapters,
    addCourseVideo,
    addCourseVideoChunked,
    addVideoLoading,
    isUploadingChunks,
    resetStep2,
    getCourseChapters,
    getCourseChaptersLoading,
    courseChaptersData,
    fetchCourse,
    courseData,
    lastLoginDates,
    setCourseData,
    updateCourseId,
    setUpdateCourseId,
    updateCourseChapter,
    updateChapterLoading,
    updateVideoLoading,
    updateCourseVideo,
    isOpenSuccess,
    onOpenSuccess,
    onCloseSuccess,
    progress,
    setProgress,
    uploadCourseEnrollData,
    addCourseEnrollLoading,
    watchLaterLoading,
    uploadCourseProgressData,
    courseProgressData,
    deleteVideoLoading,
    deleteCourseVideo,
    updateCourseEnrollData,
    getCourseProgressById,
    getCourseProgressLoading,
    duration,
    videoData,
    fetchVideoUrlById,
    step1Data,
    step2Data,
    step3Data,
    freeCoursesData,
    freeCourseData,
    fetchFreeCourses,
    fetchFreeCourse,
    fetchFreeCategories,
    freeCategoriesData,
    updateCourseVideoChunked,
  };
};
